import get from 'lodash/get'
import { isMobileOnly } from 'mobile-device-detect'

import { formatDate, startOfDate } from 'utils/dates'
import { formatHexColorCode } from 'utils/images'
import { CONTENT_TYPES, contentfulClient } from 'utils/sdks/contentful'

import {
  defaultColorBlue,
  editorialLayouts,
  styleStoriesLayouts,
} from '../constants'

const SPLIT_SEPARATOR = ';'
const BRAND_ID_PREFIX = 'brand:'
const BRAND_NAME_PREFIX = 'brandName:'
const EXPLORE_THE_SHOW_TAB_INDEX = 0
const SEARCH_AND_FILTER_TAB_INDEX = 1

const utils = {
  normalizeData: (entries, contentType) => {
    // https://contentful.github.io/contentful.js/contentful/7.14.5/ContentfulClientAPI.html#.parseEntries
    const parsedEntries = contentfulClient.parseEntries(entries)
    if (contentType === CONTENT_TYPES.LANDING) {
      return utils.normalizeLanding(parsedEntries)
    }
    if (contentType === CONTENT_TYPES.EVENTS) {
      const normalizedEvents = utils.normalizeEvents(parsedEntries.items)
      if (normalizedEvents.length === 0) {
        throw new Error(
          'Could not find associated event in Contentful. Please check that the slug is correct.',
        )
      }
      if (normalizedEvents.length === 1) {
        return normalizedEvents[0]
      }
      return normalizedEvents
    }
    if (contentType === CONTENT_TYPES.STYLE_STORY_LAYOUTS) {
      return utils.normalizeStyleStoryLayouts(parsedEntries.items)
    }
    return parsedEntries
  },

  replaceProxyUrl: (resourceUrl) => {
    const domainMappings = [
      {
        original: 'images.ctfassets.net',
        proxy: 'cdn-contentful-images.jooraccess.com',
      },
      {
        original: 'downloads.ctfassets.net',
        proxy: 'cdn-contentful-downloads.jooraccess.com',
      },
      {
        original: 'videos.ctfassets.net',
        proxy: 'cdn-contentful-videos.jooraccess.com',
      },
    ]

    const foundMapping = domainMappings.find((mapping) =>
      resourceUrl.includes(mapping.original),
    )

    if (foundMapping) {
      return resourceUrl.replace(foundMapping.original, foundMapping.proxy)
    }

    return resourceUrl
  },

  normalizeEvents: (entries) =>
    entries.map(({ fields: event }) => {
      return {
        eventLayout: get(event, 'eventLayout', 'regular-event-layout'),
        isRetailerShow: Boolean(event.isRetailerShow),
        isPrivate: Boolean(event.private),
        hideCategoriesFilter: Boolean(event.hideCategoriesFilter),
        defaultSelectedTab:
          // we need to check in this way because the field is not required
          event.defaultSelectedTab === undefined
            ? SEARCH_AND_FILTER_TAB_INDEX
            : Boolean(event.defaultSelectedTab)
              ? SEARCH_AND_FILTER_TAB_INDEX
              : EXPLORE_THE_SHOW_TAB_INDEX,
        showFilterPlaceholder: event.showFilterPlaceholder ?? '',
        title: event.title,
        slug: event.slug,
        images: {
          tile: utils.replaceProxyUrl(
            get(event, 'tileImage.fields.file.url', ''),
          ),
          mainHero: utils.replaceProxyUrl(
            get(event, 'mainHeroImage.fields.file.url', ''),
          ),
          landingHero: utils.replaceProxyUrl(
            get(event, 'landingHeroImage.fields.file.url', ''),
          ),
          logo: utils.replaceProxyUrl(
            get(event, 'logoImage.fields.file.url', ''),
          ),
        },
        pageColors: utils._normalizePageColors(event),
        redirect: event.redirectUrl,
        register: get(event, 'registerUrl', ''),
        dates: utils._normalizeDates(
          event.startDate,
          event.endDate,
          event.monthOnly,
        ),
        startDate: event.monthOnly
          ? startOfDate(event.startDate, 'month')
          : event.startDate,
        archived: get(event, 'archived', false),
        showHeroAndLogoOnly: event.showHeroAndLogoOnly,
        styleStoriesLayout: utils._normalizeStyleStoriesLayout(event),
        editorialLayout: utils._normalizeEditorialLayout(event),
        styleStories: event.styleStories
          ? utils._normalizeStyleStories(event.styleStories)
          : [],
        header: event.styleStoriesHeader || '',
        blurb: event.styleStoriesBlurb || '',
        showBlurb: get(event, 'showDiscoveryBlurb', false),
        showRooms: event.showrooms
          ? utils._normalizeShowrooms(event.showrooms)
          : [],
        social: { ...get(event, 'socialMedia.fields', {}) },
        useCustomNavigation: event.useCustomNavigation,
        customNavigationColor: get(event, 'customNavigationColor', '#FFFFFF'),
        customNavigationLogo: utils.replaceProxyUrl(
          get(event, 'customNavigationLogo.fields.file.url', ''),
        ),
        customNavigationJoorLogo: utils.replaceProxyUrl(
          get(event, 'customNavigationJoorLogo.fields.file.url', ''),
        ),
        description: get(event, 'description', ''),
        badge: get(event, 'badge.fields.badgeType', ''),
      }
    }),

  /*
   * The order of active events from Contentful needs to be reversed so that the
   * order displayed in Contentful matches the order displayed on the landing page.
   * Multi-event support on mobile is displayed differently from desktop, so the
   * order does not need to be reversed.
   */
  normalizeLanding: ({ items }) => ({
    // NOTE: CONTENTFUL Validation enforces 1 "passport landing" entry always exists, and only 1.
    featuredEvents: utils.normalizeEvents(
      get(items[0], 'fields.featuredEvents', []),
    ),
    activeEvents: isMobileOnly
      ? utils.normalizeEvents(get(items[0], 'fields.activeEvents', []))
      : utils
          .normalizeEvents(get(items[0], 'fields.activeEvents', []))
          .reverse(),
    marketplaceEvents: utils.normalizeEvents(
      get(items[0], 'fields.marketplaceEvents', []),
    ),
    retailerShows: utils.normalizeEvents(
      get(items[0], 'fields.retailerShows', []),
    ),
    upcomingEvents: utils.normalizeEvents(
      get(items[0], 'fields.upcomingEvents', []),
    ),
    partners: utils._normalizePartners(get(items[0], 'fields.partners', [])),
  }),

  normalizeStyleStoryLayouts: (items) =>
    items.reduce((layouts, item) => {
      if (get(item, 'fields.layout', '')) {
        // eslint-disable-next-line no-param-reassign
        layouts[item.fields.layout] = {
          numStories: get(item, 'fields.numStories', []),
        }
      }
      return layouts
    }, {}),

  _normalizePartners: (partners) =>
    partners.map((partner) => ({
      logo: utils.replaceProxyUrl(
        get(partner, 'fields.logo.fields.file.url', ''),
      ),
    })),

  _normalizeDates: (startDate, endDate, monthOnly) => {
    const normalizedFormat = monthOnly ? 'MMMM' : 'MMMM D'
    const normalizedStart = formatDate(startDate, normalizedFormat)
    const normalizedEnd = formatDate(endDate, normalizedFormat)

    if (startDate === endDate || !endDate) {
      return normalizedStart
    }
    return `${normalizedStart} - ${normalizedEnd}`
  },

  _normalizeShowrooms: (entries) =>
    entries.map(({ fields: showroom }) => ({
      brands: showroom.brands
        ? utils._normalizeShowroomBrands(showroom.brands)
        : [],
      eventLandingImage: utils.replaceProxyUrl(
        get(showroom, 'eventLandingImage.fields.file.url', ''),
      ),
      heroImage: utils.replaceProxyUrl(
        get(showroom, 'heroImage.fields.file.url', ''),
      ),
      longBlurb: showroom.longBlurb,
      slug: showroom.slug,
      title: showroom.title,
    })),

  _normalizeShowroomBrands: (entries) =>
    entries.map(({ fields: brand }) => ({
      brandId: brand.brandId,
      name: brand.name,
      heroImage: utils.replaceProxyUrl(
        get(brand, 'heroImage.fields.file.url', ''),
      ),
      slug: get(brand, 'slug', ''),
    })),

  // TODO (ch80183) Add unit tests
  _normalizeStyleStories: (entries) => {
    const unresolvedStyleStoryIds = entries
      .filter((entry) => !get(entry, 'fields'))
      .map((entry) => get(entry, 'sys.id'))
      .filter(Boolean)

    if (unresolvedStyleStoryIds.length > 0) {
      console.warn(
        '[Passport contentful] unresolved styleStories references:',
        unresolvedStyleStoryIds,
      )
    }

    return entries
      .filter((entry) => Boolean(get(entry, 'fields')))
      .map(({ fields: styleStory }) => ({
        title: styleStory.title,
        slug: styleStory.slug,
        shortBlurb: styleStory.shortBlurb,
        fullBlurb: styleStory.fullBlurb,
        color: formatHexColorCode(get(styleStory, 'color', defaultColorBlue)),
        images: {
          // https://www.contentful.com/developers/docs/references/images-api/#/reference
          hero: utils.replaceProxyUrl(
            get(styleStory, 'heroImage.fields.file.url', ''),
          ),
          highlight: utils.replaceProxyUrl(
            get(styleStory, 'highlightImage.fields.file.url', ''),
          ),
          highlightDimensions: get(
            styleStory,
            'highlightImage.fields.file.details.image',
            { width: '100%', height: '100%' },
          ),
          altHighlight: utils.replaceProxyUrl(
            get(styleStory, 'altHighlightImage.fields.file.url', ''),
          ),
          editorial: get(styleStory, 'editorialImages', []).map((image) =>
            utils.replaceProxyUrl(get(image, 'fields.file.url', '')),
          ),
          trendEditorial: get(styleStory, 'editorialImages', []).map(
            (image) => ({
              url: utils.replaceProxyUrl(get(image, 'fields.file.url', '')),
              brandData: utils._parseEditorialDescription(
                get(image, 'fields.description', ''),
              ),
            }),
          ),
        },
      }))
  },

  _normalizeStyleStoriesLayout: (event) => {
    const stringFromCF = get(
      event,
      'styleStoriesLayout',
      styleStoriesLayouts.V0,
    )
    if (stringFromCF.includes(styleStoriesLayouts.V1)) {
      return styleStoriesLayouts.V1
    }
    if (stringFromCF.includes(styleStoriesLayouts.V2)) {
      return styleStoriesLayouts.V2
    }
    if (stringFromCF.includes(styleStoriesLayouts.V3)) {
      return styleStoriesLayouts.V3
    }
    return styleStoriesLayouts.V0
  },

  _normalizeEditorialLayout: (event) => {
    const stringFromCF = get(event, 'editorialLayout', editorialLayouts.V0)
    return stringFromCF.includes(editorialLayouts.V1)
      ? editorialLayouts.V1
      : editorialLayouts.V0
  },

  /* In Contentful, the following reg exp is used as validation for the event color attributes. If the input
   * from CF does not pass the reg exp, CF won't save it and we will get an empty string here.
   *
   * ^#?[0-9a-fA-F]{8}$|#?[0-9a-fA-F]{6}$|#?[0-9a-fA-F]{4}$|#?[0-9a-fA-F]{3}$
   */
  _normalizePageColors: (event) => {
    const primaryHeader =
      formatHexColorCode(get(event, 'primaryHeaderColor', '')) ||
      defaultColorBlue
    const secondaryHeader =
      formatHexColorCode(get(event, 'secondaryHeaderColor', '')) ||
      primaryHeader
    const tertiaryHeader =
      formatHexColorCode(get(event, 'tertiaryHeaderColor', '')) || primaryHeader
    const text = formatHexColorCode(get(event, 'textColor', '')) || '#444444'
    const button =
      formatHexColorCode(get(event, 'buttonColor', '')) || primaryHeader
    const footer =
      formatHexColorCode(get(event, 'footerColor', '')) || primaryHeader

    return {
      primaryHeader,
      secondaryHeader,
      tertiaryHeader,
      text,
      button,
      footer,
    }
  },

  /**
   * We agreed to use the description field to add info for the trend editorial images. In this image
   * field we are gonna include the brand id and the brand name so teh front end can generate the links
   * to the brand profiles and add the brand name to the footer of the image link
   *
   * @param {string} description String given in contentful which should match the next template:
   * "brand:<brandId>;brandName:<band display name>"
   * @returns the `brandId` and `brandName` extracted from the description string
   * @return undefined if the description strings lacks of the parts for the parsing: ';' , 'brand' or 'brandName'
   */
  _parseEditorialDescription: (description) => {
    if (
      !description.includes(SPLIT_SEPARATOR) ||
      !description.includes(BRAND_ID_PREFIX) ||
      !description.includes(BRAND_NAME_PREFIX)
    ) {
      return
    }

    const parts = description.split(SPLIT_SEPARATOR)

    let brandId = null
    let brandName = null

    for (let part of parts) {
      if (part.includes(BRAND_ID_PREFIX)) {
        brandId = part.split(BRAND_ID_PREFIX)[1]
      }
      if (part.includes(BRAND_NAME_PREFIX)) {
        brandName = part.split(BRAND_NAME_PREFIX)[1]
      }
    }

    return { brandId, brandName }
  },
}

export default utils
